/**
 * Glossary Add/Edit Form Dialog — extracted from AdminGlossaryTab.
 *
 * Receives a `formApi` bundle to avoid prop-drilling individual handlers.
 * The same component is reused for both add & edit modes — the parent
 * component decides which dialog state to bind via the `mode` prop.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { BookOpen, Globe, FileText, Tag, Plus, Trash2 } from 'lucide-react';

const EXAM_CONTEXTS_FALLBACK = ['PMP', 'CAPM', 'Scrum', 'PSM', 'PSPO', 'SAFe', 'PRINCE2', 'ITIL'];

const GlossaryFormDialog = ({
  mode = 'add',                // 'add' | 'edit'
  open,
  onOpenChange,
  formData,
  setFormData,
  // context-definition input row
  newContextExam,
  setNewContextExam,
  newContextDef,
  setNewContextDef,
  newContextRef,
  setNewContextRef,
  addContextDefinition,
  removeContextDefinition,
  // references
  newReference,
  setNewReference,
  addReference,
  removeReference,
  // tags
  newTag,
  setNewTag,
  addTag,
  removeTag,
  // submit handlers
  onSubmit,
  // metadata sources
  categories = [],
  examContexts = EXAM_CONTEXTS_FALLBACK,
}) => {
  const isEdit = mode === 'edit';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            {isEdit ? 'Begriff bearbeiten' : 'Neuen Begriff hinzufügen'}
            <Badge variant="outline" className="ml-2">GOSSA v2.0</Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basis</TabsTrigger>
            <TabsTrigger value="contexts">Kontext-Definitionen</TabsTrigger>
            <TabsTrigger value="references">Referenzen</TabsTrigger>
            <TabsTrigger value="meta">Metadaten</TabsTrigger>
          </TabsList>

          {/* Basic */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div>
              <Label className="text-base font-semibold">Begriff (Englisch) *</Label>
              <Input
                value={formData.term}
                onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                placeholder="z.B. Sprint, Stakeholder, WBS"
                className="mt-1"
                data-testid="term-input"
              />
              <p className="text-xs text-slate-500 mt-1">Case-insensitive Duplikatprüfung aktiv</p>
            </div>

            <div>
              <Label className="text-base font-semibold">Master-Definition *</Label>
              <Textarea
                value={formData.master_definition}
                onChange={(e) => setFormData({ ...formData, master_definition: e.target.value })}
                placeholder="Normative Kerndefinition des Begriffs..."
                rows={5}
                className="mt-1"
                data-testid="master-definition-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Standard-Definition, wenn keine kontextspezifische verfügbar ist
              </p>
            </div>

            <div>
              <Label>Primäre Quelle</Label>
              <Input
                value={formData.primary_source}
                onChange={(e) => setFormData({ ...formData, primary_source: e.target.value })}
                placeholder="z.B. Scrum Guide 2020, PMBOK 7th Edition"
                className="mt-1"
                data-testid="primary-source-input"
              />
            </div>
          </TabsContent>

          {/* Context Definitions */}
          <TabsContent value="contexts" className="space-y-4 mt-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Kontext-spezifische Definitionen
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                Fügen Sie exam-spezifische Definitionen hinzu. Benutzer sehen automatisch die Definition passend zu ihrem ausgewählten Track.
              </p>

              {Object.keys(formData.context_definitions).length > 0 && (
                <div className="space-y-2 mb-4">
                  {Object.entries(formData.context_definitions).map(([exam, ctx]) => (
                    <div
                      key={exam}
                      className="p-3 bg-white dark:bg-slate-800 rounded border flex justify-between items-start"
                    >
                      <div className="flex-1">
                        <Badge className="mb-1">{exam}</Badge>
                        <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                          {ctx.definition}
                        </p>
                        {ctx.reference && (
                          <p className="text-xs text-slate-400 mt-1">Ref: {ctx.reference}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeContextDefinition(exam)}
                        className="text-red-500 ml-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded">
                <div className="grid grid-cols-3 gap-2">
                  <Select value={newContextExam} onValueChange={setNewContextExam}>
                    <SelectTrigger>
                      <SelectValue placeholder="Exam wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {examContexts
                        .filter((e) => !formData.context_definitions[e])
                        .map((exam) => (
                          <SelectItem key={exam} value={exam}>{exam}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={newContextRef}
                    onChange={(e) => setNewContextRef(e.target.value)}
                    placeholder="Referenz (optional)"
                    className="col-span-2"
                  />
                </div>
                <Textarea
                  value={newContextDef}
                  onChange={(e) => setNewContextDef(e.target.value)}
                  placeholder="Kontext-spezifische Definition..."
                  rows={2}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addContextDefinition}
                  disabled={!newContextExam || !newContextDef}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Kontext hinzufügen
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* References */}
          <TabsContent value="references" className="space-y-4 mt-4">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Referenzen / Quellen
              </h4>

              {formData.references.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {formData.references.map((ref) => (
                    <Badge
                      key={`ref-${ref}`}
                      variant="secondary"
                      className="flex items-center gap-1 pr-1"
                    >
                      {ref}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeReference(ref)}
                        className="h-4 w-4 p-0 ml-1 hover:bg-red-100"
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  value={newReference}
                  onChange={(e) => setNewReference(e.target.value)}
                  placeholder="z.B. CAPM Glossary 2024, PMBOK Guide 7th Ed"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addReference())}
                />
                <Button variant="outline" size="sm" onClick={addReference}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Metadata */}
          <TabsContent value="meta" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kategorie</Label>
                <Select
                  value={formData.category}
                  onValueChange={(val) => setFormData({ ...formData, category: val })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Version</Label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Tags
              </Label>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 mb-2">
                  {formData.tags.map((tag) => (
                    <Badge
                      key={`tag-${tag}`}
                      variant="outline"
                      className="flex items-center gap-1 pr-1"
                    >
                      {tag}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTag(tag)}
                        className="h-4 w-4 p-0 ml-1 hover:bg-red-100"
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-1">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Tag hinzufügen..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button variant="outline" size="sm" onClick={addTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <div>
                <Label
                  htmlFor="do_not_translate"
                  className="font-semibold text-orange-800 dark:text-orange-200"
                >
                  Nicht übersetzen
                </Label>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  Framework-Begriffe (Sprint, Scrum, WBS) sollten nicht übersetzt werden
                </p>
              </div>
              <Switch
                id="do_not_translate"
                checked={formData.do_not_translate}
                onCheckedChange={(checked) => setFormData({ ...formData, do_not_translate: checked })}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={onSubmit} className="bg-indigo-600 hover:bg-indigo-700">
            {isEdit ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GlossaryFormDialog;
